import { BUSINESS_CHECKING_ACCOUNT } from 'axp-product-ducks';

import { BUSINESS_SAVINGS_ACCOUNT, STATUS } from '../constants';
import { isCheckingUrl, isSavingsUrl } from './businessBankingRouteUtil';

const isBusinessBankingProduct = (product) =>
  [BUSINESS_CHECKING_ACCOUNT, BUSINESS_SAVINGS_ACCOUNT].includes(product.type);

const isBusinessSavingsProduct = (product) =>
  product.type === BUSINESS_SAVINGS_ACCOUNT;

const isBusinessCheckingProduct = (product) =>
  product.type === BUSINESS_CHECKING_ACCOUNT;

const isBcaProduct = (product) => product === BUSINESS_CHECKING_ACCOUNT;

const isBsaProduct = (product) => product === BUSINESS_SAVINGS_ACCOUNT;

const hasBcaProduct = (productsResult) =>
  productsResult.some(isBusinessCheckingProduct);

const hasBsaProduct = (productsResult) =>
  productsResult.some(isBusinessSavingsProduct);

/**
 * Checks whether the given account token belongs to a business banking product.
 *
 * @param {Array<object>} products - List from ReadCustomerProducts.
 * @param {string|null} productId - Account id from route/store.
 * @returns {boolean} `true` when the token maps to a checking/savings account.
 */
const isProductBusinessBanking = (products, productId) => {
  if (productId === null || !Array.isArray(products)) {
    return false;
  }

  return products.some(
    (product) =>
      product.opaqueAccountId === productId && isBusinessBankingProduct(product)
  );
};

/**
 * Selects a default product for routing and initial account selection.
 *
 * Preference order:
 * 1) First active business banking product matching `type`
 * 2) First active business banking product of any type
 * 3) First product in the list
 *
 * @param {Array<object>} products - List from ReadCustomerProducts.
 * @param {string|undefined} type - Preferred product type from path/token context.
 * @returns {object|undefined} The selected default product.
 */
const getDefaultProduct = (products, type) => {
  const getActiveBusinessBankingProduct = (product) =>
    isBusinessBankingProduct(product) &&
    product.status === STATUS.ACTIVE &&
    product.type === type;

  const getFirstActiveProduct = (product) =>
    isBusinessBankingProduct(product) && product.status === STATUS.ACTIVE;

  return (
    products.find(getActiveBusinessBankingProduct) ||
    products.find(getFirstActiveProduct) ||
    products[0]
  );
};

/**
 * Finds a product by opaqueAccountId.
 *
 * @param {Array<object>} products - List from ReadCustomerProducts.
 * @param {string|null|undefined} id - Account token from route/store.
 * @returns {object|undefined} Matching product, if found.
 */
const getProduct = (products, id) => {
  return products.find((product) => product?.opaqueAccountId === id);
};

/**
 * Resolves the intended product type from the current route.
 * Relying on the route params does not always return a product.
 *
 * @param {string} pathname - Current route pathname.
 * @returns {string|undefined} Product type for savings/checking routes, otherwise `undefined`.
 */
const getTypeFromPath = (pathname) => {
  if (isSavingsUrl(pathname)) {
    return BUSINESS_SAVINGS_ACCOUNT;
  }
  if (isCheckingUrl(pathname)) {
    return BUSINESS_CHECKING_ACCOUNT;
  }
  return undefined;
};

/**
 * Validates whether the product type exists in customer products.
 *
 * @param {Array<object>} products - List from ReadCustomerProducts.
 * @param {string|undefined} type - Product type inferred from route path.
 * @returns {boolean} `true` when the path product type is present.
 */
const isProductValid = (products, type) => {
  if (isBsaProduct(type)) {
    return hasBsaProduct(products);
  }
  if (isBcaProduct(type)) {
    return hasBcaProduct(products);
  }
  return false;
};

/**
 * Validates route/account token consistency against available products.
 *
 * Rules:
 * - If an account token is present, it must exist and match the route product type.
 * - The product type inferred from path must exist in the customer's products.
 *
 * @param {Array<object>} products - List from ReadCustomerProducts.
 * @param {string|undefined|null} accountIdentifier - Account token from route params.
 * @param {string|undefined} productTypeFromPath - Product type inferred from route path.
 * @returns {boolean} `true` when route token+path context is valid.
 */
const isAccountTokenOrPathValid = (
  products,
  accountIdentifier,
  productTypeFromPath
) => {
  const product = getProduct(products, accountIdentifier);
  const productFromPathValid = isProductValid(products, productTypeFromPath);

  if (
    accountIdentifier &&
    (!product || product?.type !== productTypeFromPath)
  ) {
    return false;
  }

  if (!productFromPathValid) {
    return false;
  }

  return true;
};

/**
 * Builds product selection context used by route `onEnter` guards.
 *
 * @param {Array<object>} products - List from ReadCustomerProducts.
 * @param {string} currentPath - Current route pathname.
 * @param {string|undefined|null} accountIdentifier - Account token from route params.
 * @returns {{
 *   newSelectedProductObj: object|undefined,
 *   defaultProduct: object|undefined,
 *   isAccountTokenOrPathValid: boolean,
 *   productTypeFromPath: string|undefined
 * }}
 */
const getProductRouteContext = (products, currentPath, accountIdentifier) => {
  const newSelectedProductObj = getProduct(products, accountIdentifier);
  const productTypeFromId = newSelectedProductObj?.type;
  const productTypeFromPath = getTypeFromPath(currentPath);

  return {
    newSelectedProductObj,
    defaultProduct: getDefaultProduct(
      products,
      productTypeFromId || productTypeFromPath
    ),
    isAccountTokenOrPathValid: isAccountTokenOrPathValid(
      products,
      accountIdentifier,
      productTypeFromPath
    ),
    productTypeFromPath,
  };
};

export {
  getDefaultProduct,
  getProduct,
  getTypeFromPath,
  getProductRouteContext,
  hasBcaProduct,
  hasBsaProduct,
  isBcaProduct,
  isBsaProduct,
  isProductBusinessBanking,
};
